import React, { useCallback, useEffect, useState } from 'react'
import { IoSave } from "react-icons/io5";
import { Button } from '../../components/ui/button'
import { Spinner } from '../../components/ui/spinner';
import SelectInput, { SelectOption } from '../../components/form/SelectInput';
import { createShippingData, CreateShippingErrors, CreateShippingPayload, fetchOfficeOptions, validateCreateShippingFormPayload } from './ShippingServices';
import { DatePicker } from '../../components/form/DatePicker';
import { TextAreaInput, TextInput } from '../../components/form/Input';
import { rupiahFormat } from '../../lib/utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import Alert, { AlertPropsType } from '../../components/ui/alert';
import moment from 'moment';
import { useNavigate } from 'react-router';

const PAYMENT_STATUS_OPTIONS = [
  { value: "paid", label: "LUNAS" },
  { value: "unpaid", label: "BELUM BAYAR" },
];

const ITEM_UNIT_OPTIONS = [
  { value: "colly", label: "Colly" },
  { value: "lembar", label: "Lembar" },
  { value: "potong", label: "Potong" },
  { value: "roll", label: "Roll" },
  { value: "kotak", label: "Kotak" },
  { value: "unit", label: "Unit" },
  { value: "amplop", label: "Amplop" },
];

const CreateShippingForm = () => {
  const navigate = useNavigate();

  const { user } = useSelector((state: RootState) => state.auth);

  const [officeOptions, setOfficeOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertPropsType | null>(null);
  const [errors, setErrors] = useState<CreateShippingErrors | null>(null);

  const [senderName, setSenderName] = useState<string>("");
  const [senderAddress, setSenderAddress] = useState<string>("");
  const [senderPhone, setSenderPhone] = useState<string>("");
  const [recipientName, setRecipientName] = useState<string>("");
  const [recipientAddress, setRecipientAddress] = useState<string>("");
  const [recipientPhone, setRecipientPhone] = useState<string>("");
  const [originOffice, setOriginOffice] = useState<string | undefined>();
  const [destinationOffice, setDestinationOffice] = useState<string | undefined>();
  const [selectedShippingDate, setSelectedShippingDate] = useState<Date | undefined>(new Date());
  const [quantity, setQuantity] = useState<number>();
  const [itemName, setItemName] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [packing, setPacking] = useState<string>("");
  const [itemUnit, setItemUnit] = useState<string>("");
  const [weightKg, setWeightKg] = useState<number>();
  const [subTotalPrice, setSubTotalPrice] = useState<number | ''>('');
  const [discountPrice, setDiscountPrice] = useState<number| ''>('');
  const [paymentStatus, setPaymentStatus] = useState<string>("");

  const getOfficeOptions = useCallback(async () => {
    setIsLoading(true);
    const response = await fetchOfficeOptions();
    if (response.status === 200) {
      const { data } = response.message;
      setOfficeOptions(data);

      if (user && user.office && user.role !== "super-admin") {
        const defaultOriginOffice = data.find((el: SelectOption) => el.value === user.office._id);
        setOriginOffice(defaultOriginOffice.value);
      };
    };

    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    getOfficeOptions();
    return () => { }
  }, [getOfficeOptions]);

  const deleteErrorHandler = (event: React.ChangeEvent<HTMLInputElement> | string) => {
    if (errors) {
      const newErrors = errors;
      const removedProperty = typeof event === "string" ? event : event.target.name;
      delete newErrors[removedProperty];
      setErrors(newErrors);
    };
  };

  const resetFormValues = () => {
    if(user && user.office && user.role !== "super-admin") {
      setOriginOffice(user.office._id);
    } else {
      setOriginOffice('');
    };

    setSenderName("");
    setSenderAddress("");
    setSenderPhone("");
    setRecipientName("");
    setRecipientAddress("");
    setRecipientPhone("");
    setDestinationOffice('');
    setSelectedShippingDate(new Date());
    setQuantity(0);
    setItemName("");
    setNotes("");
    setPacking("");
    setItemUnit("");
    setWeightKg(0);
    setSubTotalPrice('');
    setDiscountPrice('');
    setPaymentStatus("");

    navigate("shipping");
  }

  // const formatRupiah = (angka: string) => {
  //   let numberString = angka.replace(/[^,\d]/g, '').toString();
  //   let split = numberString.split(',');
  //   let sisa = split[0].length % 3;
  //   let rupiah = split[0].substr(0, sisa);
  //   let ribuan = split[0].substr(sisa).match(/\d{3}/gi);

  //   if (ribuan) {
  //     let separator = sisa ? '.' : '';
  //     rupiah += separator + ribuan.join('.');
  //   }

  //   rupiah = split[1] !== undefined ? rupiah + ',' + split[1] : rupiah;
  //   return rupiah;
  // };

  // Fungsi konversi number ke visual format Rupiah
  const formatDisplay = (num: number | ''): string => {
    if (num === '') return '';
    return new Intl.NumberFormat('id-ID').format(num);
  };

  const handleCreateShipping = async (payload: CreateShippingPayload) => {
    const successRequest = await createShippingData(payload);
    if (!successRequest) {
      setIsLoading(false);
      setAlert({
        variant: "error",
        message: "Terjadi kesalahan pada server. Mohon coba beberapa saat lagi!"
      });
      return;
    }

    const { status, message } = successRequest;

    if (status === 200) {
      resetFormValues();
      setIsLoading(false);
      setAlert({
        variant: "success",
        message: "Data berhasil tersimpan."
      });
    } else {
      setIsLoading(false);
      setAlert({
        variant: "error",
        message
      });
    };
  };

  const submitHandler = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setAlert(null);

    const payload = {
      item_name: itemName,
      quantity: Number(quantity),
      weight_kg: Number(weightKg),
      packing,
      item_unit: itemUnit,
      total_price: Number(subTotalPrice) - Number(discountPrice),
      discount_price: Number(discountPrice),
      payment_status: paymentStatus,
      notes,
      shipping_date: moment(selectedShippingDate).format("DD/MM/YYYY"),
      origin_office_id: originOffice,
      destination_office_id: destinationOffice,
      origin_customer_data: {
        sender_name: senderName,
        sender_phone: senderPhone.toString(),
        sender_address: senderAddress
      },
      destination_customer_data: {
        recipient_name: recipientName,
        recipient_phone: recipientPhone.toString(),
        recipient_address: recipientAddress
      },
      admin_maker: user?._id
    };

    const { isValid, formErrors } = validateCreateShippingFormPayload({ ...payload });

    if (isValid) {
      setIsLoading(false)
      handleCreateShipping(payload);
    } else {
      setIsLoading(false);
      setErrors(formErrors);
      setAlert({
        variant: "error",
        message: "Lengkapi formulir dengan benar"
      });
    };
  };

  return (
    <div>
      <form onSubmit={submitHandler}>
        <div className='grid lg:grid-cols-2 gap-2.5'>
          <div className='flex flex-col gap-1.5'>
            <label className="font-semibold">Kantor Asal</label>
            <SelectInput
              label="Kantor Asal"
              name='originOffice'
              placeholder='Pilih kantor asal'
              disabled={isLoading || user?.role !== "super-admin"}
              value={originOffice}
              error={errors?.originOffice}
              onChange={(value) => {
                deleteErrorHandler("originOffice");
                setOriginOffice(value);
              }}
              options={officeOptions.filter((el: SelectOption) => el.value !== destinationOffice)}
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className="font-semibold">Kantor Tujuan</label>
            <SelectInput
              label="Kantor Tujuan"
              name='destinationOffice'
              placeholder='Pilih kantor tujuan'
              disabled={isLoading}
              value={destinationOffice}
              error={errors?.destinationOffice}
              onChange={(value) => {
                deleteErrorHandler("destinationOffice");
                setDestinationOffice(value);
              }}
              options={officeOptions.filter((el: SelectOption) => el.value !== originOffice)}
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className="font-semibold">Tanggal Pengiriman</label>
            <DatePicker
              mode="single"
              value={selectedShippingDate}
              disabled={isLoading}
              minDate={new Date()}
              onChange={(value) => setSelectedShippingDate(value as Date)}
            />
          </div>
        </div>
        <hr className='my-5' />
        <div className='grid lg:grid-cols-2 gap-2.5'>
          <div className='flex flex-col gap-1.5'>
            <label className='font-semibold'>Nama Pengirim</label>
            <TextInput
              required
              name="senderName"
              value={senderName}
              placeholder="Nama pengirim"
              disabled={isLoading}
              error={errors?.senderName}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setSenderName(e.target.value)
              }}
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className='font-semibold'>No. HP Pengirim</label>
            <TextInput
              required
              name="senderPhone"
              value={senderPhone}
              placeholder="No. HP Pengirim"
              disabled={isLoading}
              error={errors?.senderPhone}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setSenderPhone(e.target.value)
              }}
            />
          </div>
          <div className='flex flex-col gap-1.5 col-span-2'>
            <label className='font-semibold'>Alamat Pengirim</label>
            <TextAreaInput
              required
              name="senderAddress"
              value={senderAddress}
              placeholder="Masukkan alamat pengirim"
              disabled={isLoading}
              error={errors?.senderAddress}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setSenderAddress(e.target.value)
              }}
            />
          </div>
        </div>
        <hr className='my-5' />
        <div className='grid lg:grid-cols-2 gap-2.5'>
          <div className='flex flex-col gap-1.5'>
            <label className='font-semibold'>Nama Penerima</label>
            <TextInput
              required
              name="recipientName"
              value={recipientName}
              placeholder="Nama penerima"
              disabled={isLoading}
              error={errors?.recipientName}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setRecipientName(e.target.value)
              }}
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className='font-semibold'>No. HP Penerima</label>
            <TextInput
              required
              name="recipientPhone"
              value={recipientPhone}
              placeholder="No. HP penerima"
              disabled={isLoading}
              error={errors?.recipientPhone}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setRecipientPhone(e.target.value)
              }}
            />
          </div>
          <div className='flex flex-col gap-1.5 col-span-2'>
            <label className='font-semibold'>Alamat Penerima</label>
            <TextAreaInput
              required
              name="recipientAddress"
              value={recipientAddress}
              placeholder="Masukkan alamat penerima"
              disabled={isLoading}
              error={errors?.recipientAddress}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setRecipientAddress(e.target.value)
              }}
            />
          </div>
        </div>
        <hr className='my-5' />
        <div className='grid lg:grid-cols-3 gap-2.5'>
          <div className='col-span-2 flex flex-col gap-1.5'>
            <label className='font-semibold'>Isi Kiriman</label>
            <TextInput
              required
              name="itemName"
              value={itemName}
              placeholder="Pakaian / elektronik / dll"
              disabled={isLoading}
              error={errors?.itemName}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setItemName(e.target.value)
              }}
            />
          </div>
          <div className='col-span-1 flex flex-col gap-1.5'>
            <label className='font-semibold'>Packing</label>
            <TextInput
              required
              name="packing"
              value={packing}
              placeholder="Kotak / potong / dll"
              disabled={isLoading}
              error={errors?.packing}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setPacking(e.target.value)
              }}
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className='font-semibold'>Jumlah Barang</label>
            <TextInput
              required
              type='number'
              name="quantity"
              value={quantity}
              placeholder="Masukkan jumlah barang"
              disabled={isLoading}
              error={errors?.quantity}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setQuantity(e.target.value)
              }}
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className='font-semibold'>Satuan</label>
            <SelectInput
              label="Satuan"
              name='itemUnit'
              placeholder='Pilih satuan barang'
              disabled={isLoading}
              value={itemUnit}
              error={errors?.itemUnit}
              onChange={(value) => {
                deleteErrorHandler("itemUnit");
                setItemUnit(value);
              }}
              options={ITEM_UNIT_OPTIONS}
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className='font-semibold'>Berat (kg)</label>
            <TextInput
              required
              type="number"
              name="weightKg"
              value={weightKg}
              placeholder="Berat barang ( kg )"
              disabled={isLoading}
              error={errors?.weightKg}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setWeightKg(e.target.value)
              }}
            />
          </div>
          <div className='col-span-3 flex flex-col gap-1.5'>
            <label className='font-semibold'>Keterangan</label>
            <TextInput
              name="notes"
              value={notes}
              placeholder="Masukkan keterangan ( jika ada )"
              disabled={isLoading}
              error={errors?.notes}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setNotes(e.target.value)
              }}
            />
          </div>
        </div>
        <hr className='my-5' />
        <div className='grid lg:grid-cols-3 gap-2.5'>
          <div className='flex flex-col gap-1.5'>
            <label className='font-semibold'>Total biaya ( Rp )</label>
            <TextInput
              required
              name="subTotalPrice"
              value={formatDisplay(subTotalPrice)}
              placeholder="Masukkan total ongkos kirim"
              disabled={isLoading}
              error={errors?.subTotalPrice}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                const rawValue = e.target.value;

                // Regex \D menghapus semua karakter non-digit
                const numberOnly = rawValue.replace(/\D/g, '');

                // Validasi nilai kosong, jika aman ubah menjadi number base-10
                setSubTotalPrice(numberOnly === '' ? '' : parseInt(numberOnly, 10));
              }}
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className='font-semibold'>Discount ( Rp )</label>
            <TextInput
              type='number'
              name="discountPrice"
              value={discountPrice}
              placeholder="Masukkan diskon ( jika ada )"
              disabled={isLoading}
              error={errors?.discountPrice}
              onChange={(e: any) => {
                deleteErrorHandler(e.target.name);
                setDiscountPrice(e.target.value);
              }}
            />
          </div>
          <div className='flex flex-col gap-1.5'>
            <label className='font-semibold'>Status Pembayaran</label>
            <SelectInput
              label="Status Pembayaran"
              name='paymentStatus'
              placeholder='Pilih status pembayaran'
              disabled={isLoading}
              value={paymentStatus}
              error={errors?.paymentStatus}
              onChange={(value) => {
                deleteErrorHandler("paymentStatus");
                setPaymentStatus(value);
              }}
              options={PAYMENT_STATUS_OPTIONS}
            />
          </div>
        </div>

        {alert && <Alert options={alert} />}

        <div className='flex items-center justify-between mt-10'>
          <div>
            <p>Total Pembayaran :</p>
            <h2 className='text-2xl font-bold'>Rp {subTotalPrice && discountPrice ? rupiahFormat(Number(subTotalPrice) - discountPrice) : 0}</h2>
          </div>
          {isLoading ?
            <div className="w-max flex items-center justify-center gap-2">
              <Spinner size="small" />
              <span>Mohon tunggu...</span>
            </div>
            : <Button
              disabled={isLoading}
              type='submit'
              className="bg-secondary-blue hover:!bg-sky-400 transition-all ease-in-out duration-300 text-white py-[10px] w-32 rounded-xl font-bold flex items-center gap-1.5 text-sm uppercase"
            >
              <>
                <IoSave size={10} />
                Simpan
              </>
            </Button>
          }
        </div>
      </form>
    </div>
  )
}

export default CreateShippingForm